import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  ReplaySubject,
  of,
  throwError,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { Contact } from '../../interfaces/contact.model';
import { PeriodicalDonationAgreementDTO } from '../../interfaces/payment.interface';
import { TaxReportOptions } from '../../interfaces/tax-report.interface';
import { UserSetting } from '../../interfaces/user-setting.interface';
import { Account } from '../../model/account.model';
import {
  PaymentDetails,
  PaymentProfile,
} from '../../model/paymentprofile.model';
import { AuthService } from '../auth/auth.service';
import { LanguageService } from '../language/language.service';
import { ProxyService } from '../proxy.service';
import { CacheService } from '../cache.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private isLoggedIn = false;
  private isBetaUser = new ReplaySubject<boolean>(1);
  public isBetaUser$ = this.isBetaUser.asObservable();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private languageService: LanguageService,
    private proxyService: ProxyService,
    private cache: CacheService
  ) {
    this.authService.getLoggedIn$().subscribe((res) => {
      this.isLoggedIn = res;
      if (!res) {
        this.resetUserCache();
      }
    });

    this.getBetaStatusFromAPI().subscribe((status) =>
      this.isBetaUser.next(status)
    );
  }

  private contact = new BehaviorSubject<Contact>(null);
  private address = new BehaviorSubject<Account>(null);
  private paymentprofile = new BehaviorSubject<PaymentProfile>(null);
  private userSettings = new BehaviorSubject<Array<UserSetting>>([]);

  public resetUserCache(): void {
    this.contact.next(null);
    this.address.next(null);
    this.paymentprofile.next(null);
    this.isBetaUser.next(false);

    this.contactRequest$ = null;
    this.addressRequest$ = null;
    this.paymentProfileRequest$ = null;
  }

  private createLoadableStream<T>(
    loader: () => Observable<T>,
    subject: BehaviorSubject<T>
  ): Observable<T> {
    return this.authService.getLoggedIn$().pipe(
      filter((loggedIn) => loggedIn),
      take(1),
      switchMap(() => {
        if (subject.value == null) {
          return loader().pipe(
            tap((val) => subject.next(val)),
            catchError((err) => {
              console.error('Failed to load', err);
              return of(null);
            })
          );
        }
        return of(subject.value);
      }),
      filter((v): v is T => !!v)
    );
  }

  private loadContact$ = this.createLoadableStream<Contact>(
    () => this.getContactFromAPI(),
    this.contact
  );

  private loadAddress$ = this.createLoadableStream<Account>(
    () => this.getAddressFromAPI(),
    this.address
  );

  private loadPaymentProfile$ = this.createLoadableStream<PaymentProfile>(
    () => this.getPaymentProfileFromAPI(),
    this.paymentprofile
  );

  private betaStatusRequest$: Observable<boolean>;

  private getBetaStatusFromAPI(): Observable<boolean> {
    if (!this.betaStatusRequest$) {
      this.betaStatusRequest$ = this.proxyService
        .get<{ status: boolean }>('auth/beta')
        .pipe(
          map((res) => res.status),
          catchError((err) => {
            console.error('Failed to fetch beta status', err);
            return of(false);
          }),
          shareReplay(1)
        );
    }

    return this.betaStatusRequest$;
  }

  public getAddress$(): Observable<Account> {
    return this.loadAddress$;
  }

  public getContact$(): Observable<Contact> {
    return this.loadContact$;
  }

  public getPaymentProfile$(): Observable<PaymentProfile> {
    return this.loadPaymentProfile$;
  }

  // #region CONTACT
  private contactRequest$: Observable<Contact>;

  private getContactFromAPI(): Observable<Contact> {
    if (!this.contactRequest$) {
      this.contactRequest$ = this.http
        .get<Contact>(`${environment.apiUrl}auth/contact`, {
          withCredentials: true,
        })
        .pipe(
          map((c: Contact) => ({
            ...c,
            Birthdate:
              typeof c.Birthdate === 'string'
                ? new Date(c.Birthdate)
                : c.Birthdate ?? null,
          })),
          shareReplay(1),
          catchError((err) => {
            this.contactRequest$ = null;
            return throwError(() => err);
          })
        );
    }
    return this.contactRequest$;
  }

  updateContact(
    newContact: Partial<{
      Birthdate: string;
      MobilePhone: string;
      Phone: string;
      Email: string;
      newPassword: string;
      currentPassword: string;
    }>
  ) {
    this.contactRequest$ = null;
    const data: any = { ...newContact };
    return this.http
      .post<any>(`${environment.apiUrl}auth/contact`, data, {
        withCredentials: true,
      })
      .pipe(
        tap((c) => {
          const { contact } = c;
          contact.Birthdate = contact.Birthdate
            ? new Date(Date.parse(contact.Birthdate))
            : null;
          this.contact.next(null);
        })
      );
  }
  // #endregion CONTACT

  // #region ADDRESS
  private addressRequest$: Observable<Account>;

  private getAddressFromAPI(): Observable<Account> {
    if (!this.addressRequest$) {
      this.addressRequest$ = this.http
        .get<Account>(`${environment.apiUrl}auth/account`, {
          withCredentials: true,
        })
        .pipe(
          shareReplay(1),
          catchError((err) => {
            this.addressRequest$ = null;
            return throwError(() => err);
          })
        );
    }
    return this.addressRequest$;
  }

  updateAddress(newAddress: Account) {
    this.addressRequest$ = null;
    return this.http
      .post<void>(`${environment.apiUrl}auth/account`, newAddress, {
        withCredentials: true,
      })
      .pipe(tap(() => this.address.next(null)));
  }
  // #endregion ADDRESS

  // #region PAYMENT
  private paymentProfileRequest$: Observable<PaymentProfile>;

  private getPaymentProfileFromAPI(): Observable<PaymentProfile> {
    if (!this.paymentProfileRequest$) {
      this.paymentProfileRequest$ = this.http
        .get<PaymentProfile>(
          `${environment.apiUrl}auth/paymentProfile?new=true`,
          {
            withCredentials: true,
          }
        )
        .pipe(
          shareReplay(1),
          catchError((err) => {
            this.paymentProfileRequest$ = null;
            return throwError(() => err);
          })
        );
    }
    return this.paymentProfileRequest$;
  }

  updatePaymentProfile(newProfile: Partial<PaymentDetails>) {
    this.paymentProfileRequest$ = null;
    return this.http
      .post<void>(`${environment.apiUrl}auth/paymentProfile`, newProfile, {
        withCredentials: true,
      })
      .pipe(
        tap(() => this.paymentprofile.next(null)),
        mergeMap((data) => this.getPaymentProfileFromAPI()),
        catchError((err) => of(err))
      );
  }
  // #endregion PAYMENT

  register(email: string) {
    return this.http.post<any>(
      `${environment.apiUrl}onboardingEmail`,
      {
        email,
        lang: this.languageService.selected.value,
      },
      { withCredentials: true }
    );
  }

  modifyPassword(password) {
    return this.http.post<any>(
      `${environment.apiUrl}auth/modifyPassword`,
      {
        password,
      },
      { withCredentials: true }
    );
  }

  requestEmailChange(newEmail: string): Observable<void> {
    this.contactRequest$ = null;
    return this.proxyService.put<void>(
      'auth/email',
      { newEmail },
      {
        responseType: 'text' as 'json',
      }
    );
  }

  confirmMailChange(
    hash: string
  ): Observable<{ oldMail: string; newMail: string }> {
    return this.proxyService.post<{ oldMail: string; newMail: string }>(
      'no-auth/email',
      { hash }
    );
  }

  setPassword(newPassword, hash) {
    return this.http.post<any>(
      `${environment.apiUrl}setPassword`,
      {
        password: newPassword,
        data: hash,
      },
      { withCredentials: true }
    );
  }

  /**
   * Check if the hash is still valid or not
   */
  validateHash(hash: string) {
    return this.http.get(`${environment.apiUrl}hashCheck/${hash}`, {
      withCredentials: true,
    });
  }

  autoOnboarding(hash: string): Observable<{ email: string; crypto: string }> {
    return this.http.post<{ email: string; crypto: string }>(
      `${environment.apiUrl}auto-onboarding`,
      { hash },
      { withCredentials: true }
    );
  }

  forgetPassword(username) {
    return this.http.post<any>(
      `${environment.apiUrl}forgetPassword`,
      {
        username,
        lang: this.languageService.selected.value,
      },
      { withCredentials: true }
    );
  }

  /**
   * This function makes an API call to Pro6PP. A zipcode and housenumber are passed as params.
   * When a succesful result come back, the street and city are filled in the account object.
   */
  checkZipCode(
    zipCode: string,
    houseNumber: string
  ): Observable<{ city: string; street: string; country: string }> {
    const body = {
      country: 'nl',
      zipcode: zipCode.toUpperCase(),
      number: houseNumber,
    };
    if (zipCode !== '' && houseNumber !== '') {
      return this.http
        .post<any>(environment.addressUrl, body, {
          headers: { 'x-api-key': environment.apikey },
          withCredentials: true,
        })
        .pipe(
          map((res) => {
            if (res) {
              return {
                city: res.settlement,
                street: res.street,
                country: res.country,
              };
            }
            return { city: '', street: '', country: '' };
          })
        );
    }
  }

  private askConsent$: Observable<{ askConsent: boolean }>;

  shouldAskConsent(): Observable<{ askConsent: boolean }> {
    if (!this.askConsent$) {
      this.askConsent$ = this.proxyService
        .get<{ askConsent: boolean }>('auth/consent')
        .pipe(
          shareReplay(1),
          catchError(() => of({ askConsent: false }))
        );
    }
    return this.askConsent$;
  }

  updateConsent(consent: boolean): Observable<void> {
    return this.proxyService
      .post<void>(
        'auth/consent',
        { consent },
        { responseType: 'text' as 'json' }
      )
      .pipe(tap(() => (this.askConsent$ = of({ askConsent: false }))));
  }

  /**
   * Get the user settings
   *
   * @returns {Observable<Array<UserSetting>>}
   * @memberof UserService
   */
  getUserSettings(): Observable<Array<UserSetting>> {
    if (this.isLoggedIn && this.userSettings.value.length === 0) {
      this.proxyService
        .get<Array<UserSetting>>('auth/user-settings')
        .subscribe((userSettings) => {
          this.userSettings.next(userSettings);
        });
    }

    return this.userSettings
      .asObservable()
      .pipe(filter((settings) => settings.length > 0));
  }

  /**
   * Update the user settings
   *
   * @param {Array<UserSetting>} userSettings
   * @returns {Observable<Array<UserSetting>>}
   * @memberof UserService
   */
  updateUserSettings(
    userSettings: Array<UserSetting>
  ): Observable<Array<UserSetting>> {
    const body: { [key: string]: boolean } = {};

    userSettings.forEach((setting) => {
      body[setting.backendName] = setting.isSelected;
    });

    return this.proxyService
      .post<Array<UserSetting>>('auth/user-settings', body)
      .pipe(
        catchError((err) => {
          if (err.status === 304) {
            // No settings changed
            return of(this.userSettings.value);
          }

          return throwError(err);
        }),
        tap((result) => {
          this.userSettings.next(result);
        })
      );
  }

  /**
   * Check the zipcode for Belgium
   *
   * @param {string} zipCode
   * @param {string} streetName
   * @param {string} streetNumber
   * @returns {Observable<{ city: string; street: string; country: string }>}
   * @memberof UserService
   */
  checkZipCodeBe(
    zipCode: string,
    streetName: string,
    streetNumber: string
  ): Observable<{ city: string; street: string; country: string }> {
    const body = {
      country: 'be',
      zipcode: zipCode.toUpperCase(),
      street: streetName,
      number: streetNumber,
    };
    if (zipCode !== '' && streetName !== '') {
      return this.http
        .post<any>(environment.addressUrl, body, {
          headers: { 'x-api-key': environment.apikey },
          withCredentials: true,
        })
        .pipe(
          map((res) => {
            if (res.length > 0) {
              return {
                city: res.region,
                street: res.street,
                country: res.country,
              };
            }
            return { city: '', street: '', country: '' };
          })
        );
    }
  }

  /**
   * Check the zipcode for Germany
   *
   * @param {string} zipCode
   * @param {string} streetName
   * @param {string} settlementName
   * @param {string} streetNum
   * @returns {Observable<{ city: string; street: string; country: string }>}
   * @memberof UserService
   */
  checkZipCodeDe(
    zipCode: string,
    streetName: string,
    settlementName: string,
    streetNum: string
  ): Observable<{ city: string; street: string; country: string }> {
    const body = {
      country: 'de',
      zipcode: zipCode.toUpperCase(),
      street: streetName,
      city: settlementName,
      number: streetNum,
    };
    if (zipCode !== '' && settlementName !== '') {
      return this.http
        .post<any>(environment.addressUrl, body, {
          headers: { 'x-api-key': environment.apikey },
          withCredentials: true,
        })
        .pipe(
          map((res) => {
            if (res.length > 0) {
              return {
                city: res.settlement,
                street: res.street,
                country: res.country,
              };
            }
            return { city: '', street: '', country: '' };
          })
        );
    }
  }

  /**
   * get the options of the taxreport
   * */
  getTaxreportOptions(): Observable<TaxReportOptions> {
    return this.proxyService
      .get<TaxReportOptions>(`/auth/payments/tax-report/options`)
      .pipe(
        map((result) => {
          if (!result || !result.availability) {
            return result;
          }
          const newAvailability = [
            ...result.availability
              .map((item) => {
                if (Number.isNaN(+item)) {
                  return undefined;
                }
                return +item;
              })
              .filter((item) => !!item)
              .sort((a, b) => b - a),
          ];

          return { ...result, availability: newAvailability };
        })
      );
  }

  /**
   * Get a taxreport by year and language so it can be downloaded
   *
   */
  getTaxReport(year: number, variant: string): Observable<Blob> {
    return this.proxyService.get<any>(`auth/payments/tax-report`, {
      responseType: 'blob' as 'json',
      params: { year, variant },
    });
  }

  /**
   * get Periodical agreement info
   * @param year
   */
  getPeriodicalDonationAgreement(
    year: number = new Date().getFullYear()
  ): Observable<PeriodicalDonationAgreementDTO[]> {
    return this.proxyService.get<any>(
      `auth/payments/periodical-donation-agreement`,
      {
        params: { year },
      }
    );
  }

  public removeForcePasswordKey(): void {
    if (this.cache.exists('forcePasswordChange', 'local'))
      this.cache.remove('forcePasswordChange', 'local');
  }
}
